import type { FC } from 'react';
import React from 'react';
import { Box } from '@withjoy/joykit';
import type { DuoPhotoRendererProps, PhotoDuoArrangement } from './PhotoDuo.types';
import { getAspectRatio, getScreenPartition } from './PhotoDuo.utils';

/**
 * PhotoDuo's grid holds its 2/3-primary / 1/3-secondary split at every breakpoint (see
 * PhotoDuo.utils.ts's SCREEN_PARTITION) — the primary and secondary photo render at different
 * widths, so a single per-layout `sizes` estimate (SSR_HERO_SIZES.photoDuoLeft/Right) would either
 * over-fetch the secondary or under-fetch the primary. This slot knows its own `screenPartition`,
 * so it derives its own estimate directly instead — rounding up (`Math.ceil`) so the estimate
 * never falls below the actual rendered share.
 */
const getSsrSizesForSlot = (screenPartition: number): string => `${Math.ceil(screenPartition * 100)}vw`;

const getContainerHeight = (arrangement: PhotoDuoArrangement, index: number) => {
  if (arrangement === 'left') {
    return '100%';
  }
  return index === 0 ? 'auto' : '98%';
};

export const DuoPhotoRenderer: FC<DuoPhotoRendererProps> = props => {
  const { photo, arrangement, index, containerDimensions, firstItemHeight, photoRenderer: PhotoRenderer } = props;
  const aspectRatio = getAspectRatio(arrangement, index);
  const screenPartition = getScreenPartition(arrangement, index);
  const width = containerDimensions.width * screenPartition;

  return (
    <Box
      width="100%"
      height={getContainerHeight(arrangement, index)}
      aspectRatio={aspectRatio}
    >
      <PhotoRenderer
        photoUrl={photo.photoUrl}
        cropInfo={photo.cropInfo ? { ...photo.cropInfo, unit: '%' } : null}
        width={width}
        height={firstItemHeight}
        containerType="fixed-aspect"
        ssrSizes={getSsrSizesForSlot(screenPartition)}
      />
    </Box>
  );
};
